interface Point {
  tag: string;
  sent: number;
  opens: number;
  clicks: number;
}

/**
 * Verlaufsdiagramm.
 *
 * Von Hand gezeichnetes SVG statt einer Diagrammbibliothek. Drei Reihen über
 * höchstens ein Jahr Tagespunkte rechtfertigen keine 200 kB Abhängigkeit, und
 * die üblichen Bibliotheken liefern Tooltips, die auf dem Telefon nicht
 * erreichbar sind.
 *
 * Barrierefrei über eine echte Tabelle unter dem Bild: Screenreader lesen die
 * Zahlen, nicht die Kurve. Das Bild selbst ist deshalb aria-hidden.
 */
export function TrendChart({ points }: { points: Point[] }) {
  if (points.length === 0) return <p className="muted text-sm">Keine Daten im Zeitraum.</p>;

  const width = 720;
  const height = 200;
  const padding = { top: 8, right: 8, bottom: 20, left: 36 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const max = Math.max(1, ...points.map((p) => Math.max(p.sent, p.opens, p.clicks)));
  const stepX = points.length > 1 ? innerWidth / (points.length - 1) : 0;

  const path = (key: 'sent' | 'opens' | 'clicks') =>
    points
      .map((point, index) => {
        const x = padding.left + index * stepX;
        const y = padding.top + innerHeight - (point[key] / max) * innerHeight;
        return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

  // Vier Gitterlinien reichen; mehr macht das Bild unruhig, ohne genauer zu sein.
  const gridValues = [0, 0.25, 0.5, 0.75, 1].map((factor) => Math.round(max * factor));

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${width} ${height}`} className="chart__svg" aria-hidden="true">
        {gridValues.map((value, index) => {
          const y = padding.top + innerHeight - (index / 4) * innerHeight;
          return (
            <g key={value + index}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={y}
                y2={y}
                className="chart__grid"
              />
              <text x={0} y={y + 4} className="chart__tick">
                {value}
              </text>
            </g>
          );
        })}

        <path d={path('sent')} className="chart__line" data-series="sent" />
        <path d={path('opens')} className="chart__line" data-series="opens" />
        <path d={path('clicks')} className="chart__line" data-series="clicks" />

        <text x={padding.left} y={height - 4} className="chart__tick">
          {points[0]?.tag}
        </text>
        <text x={width - padding.right} y={height - 4} className="chart__tick" textAnchor="end">
          {points[points.length - 1]?.tag}
        </text>
      </svg>

      <div className="chart__legend">
        <span data-series="sent">Versendet</span>
        <span data-series="opens">Geöffnet</span>
        <span data-series="clicks">Geklickt</span>
      </div>

      <details className="chart__data">
        <summary className="text-sm">Zahlen zum Verlauf</summary>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Tag</th>
                <th scope="col">Versendet</th>
                <th scope="col">Geöffnet</th>
                <th scope="col">Geklickt</th>
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.tag}>
                  <td>{point.tag}</td>
                  <td data-numeric>{point.sent}</td>
                  <td data-numeric>{point.opens}</td>
                  <td data-numeric>{point.clicks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
